import { forwardRef, type ComponentPropsWithoutRef } from 'react'
import { Link } from 'react-router-dom'
import { issueHref, isModifiedClick } from '@/lib/issue-url'
import { useUiState } from '@/hooks/use-ui-state'

type IssueLinkProps = Omit<ComponentPropsWithoutRef<typeof Link>, 'to' | 'onClick'> & {
  number: number
  /** Run after a plain click opens the Sheet (e.g. to close an enclosing menu). */
  onPlainClick?: () => void
}

/** A real anchor to an issue. A plain left-click keeps the app's in-place
 *  behaviour — it opens the detail Sheet; Cmd/Ctrl/Shift- or middle-click fall
 *  through to the anchor so the browser opens the full page in a new tab, and
 *  right-click → "Open in new tab" works for free. The plain-click stopPropagation
 *  keeps it from also firing an enclosing row/card click handler. */
export const IssueLink = forwardRef<HTMLAnchorElement, IssueLinkProps>(function IssueLink(
  { number, onPlainClick, ...rest },
  ref,
) {
  const { setSheetIssue } = useUiState()
  return (
    <Link
      ref={ref}
      to={issueHref(number)}
      onClick={(e) => {
        if (isModifiedClick(e)) return
        e.preventDefault()
        e.stopPropagation()
        setSheetIssue(number)
        onPlainClick?.()
      }}
      {...rest}
    />
  )
})
